import type {
  ParsedSurveyCsvFlattenedAnswerValue,
  QuestionCheckboxModelData,
  QuestionChoiceType,
  QuestionDropdownModelData,
  QuestionImagepickerModelData,
  QuestionImagepickerModelDataChoice,
  QuestionRadiogroupModelData,
  QuestionRankingModelData,
  SurveyAnswerFlattenedRecord,
  SurveyAnswerRecord,
  SurveyAnswerValue,
  SurveyElementJson,
  SurveyImageAnswerValue,
  SurveyPanelAnswerValue,
} from '../types'
import { isArray } from 'es-toolkit/compat'

import { getTypedAnswerCommentValue, getTypedAnswerValue } from './answer-type.util'
import { checkULID } from './id.util'
import { hasShowCommentArea } from './question.util'

/**
 * ファイルアップロードの結果かどうかを判定する型ガード
 * todo: 回答データが image設問のものか、別の設問のタイプのものかの判断は valueがもっているpropertyで判断すべきではない気がするので、設問の設定データから型を判定するようにしたい
 */
export function isSurveyImageAnswerArray(value: SurveyAnswerValue): value is SurveyImageAnswerValue[] {
  return Array.isArray(value) && value.length > 0
    && typeof value[0] === 'object' && value[0] !== null
    && 'type' in value[0] && 'name' in value[0] && 'content' in value[0]
}

/**
 * Survey answer から指定された質問名に対する画像回答を見つける
 * ※ 通常の質問とパネル内の質問の両方をサポート
  * todo: 回答データが image設問のものか、別の設問のタイプのものかの判断は valueがもっているpropertyで判断すべきではない気がするので、設問の設定データから型を判定するようにしたい
 */
export function findImageAnswerArray(obj: SurveyAnswerRecord, questionName: string): SurveyImageAnswerValue[] | null {
  // 通常の質問
  if (questionName in obj) {
    const value = obj[questionName]
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && 'type' in value[0] && 'content' in value[0]) {
      return value as SurveyImageAnswerValue[]
    }
  }

  // パネル質問
  for (const [, value] of Object.entries(obj)) {
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      const panelAnswer = value as SurveyPanelAnswerValue
      if (questionName in panelAnswer) {
        const answerValue = panelAnswer[questionName]
        if (Array.isArray(answerValue) && answerValue.length > 0 && typeof answerValue[0] === 'object' && 'type' in answerValue[0] && 'content' in answerValue[0]) {
          return answerValue as SurveyImageAnswerValue[]
        }
      }
    }
  }

  return null
}

/**
 * 単一選択系(radio/dropdown)の質問の回答を文字列に parse
 */
function parseSingleChoiceAnswer(
  question: {
    choices: QuestionChoiceType[]
    showOtherItem?: boolean
    showCommentArea?: boolean
  },
  answerOrigin: string | undefined,
): string | undefined {
  if (answerOrigin === undefined) {
    return undefined
  }

  if (answerOrigin === 'other') {
    return 'その他'
  }

  const choice = question.choices.find((choice) => {
    const choiceCast = choice as {
      text: string
      value: string
    }
    return choiceCast.value === answerOrigin
  })
  if (choice !== undefined) {
    const choiceCast = choice as {
      text: string
      value: string
    }
    return choiceCast.text
  } else if (question.showOtherItem && question.showCommentArea) {
    return 'その他'
  }

  return undefined
}

/**
 * 単一選択系(radio/dropdown)の質問の「その他」の回答を文字列に parse
 */
function parseSingleChoiceOtherAnswer(
  question: {
    choices: QuestionChoiceType[]
    showOtherItem?: boolean
    showCommentArea?: boolean
  },
  answerOrigin: string | undefined,
  otherAnswer: string | undefined,
): string | undefined {
  if (answerOrigin === undefined || !question.showOtherItem) {
    return undefined
  }

  if (!question.showCommentArea || answerOrigin === 'other') {
    return otherAnswer
  }

  /**
   * 質問の選択肢にない回答は「その他」とみなす
   */
  const questionIds = new Set(question.choices.map((choice) => {
    if (typeof choice === 'object') {
      return choice.value
    }
    return choice
  }))
  if (!questionIds.has(answerOrigin)) {
    return answerOrigin
  }

  return undefined
}

/**
 * 単一選択系(radio/dropdown)の質問の「コメント」の回答を文字列に parse
 * NOTE:
 * - references - https://www.notion.so/asobica/surveyjs-comment-other-328c7e12081f42ea833fa0e59d004a52
 * - radiogroupと、checkboxはshowOtherItemがあり、これが、showCommentAreaも合わせて二つともtrueの場合にコメントとして入力した値の入り方がことなる
 */
function parseSingleChoiceCommentAnswer(
  question: {
    showOtherItem?: boolean
    showCommentArea?: boolean
  },
  answerOrigin: string | undefined,
  otherAnswer: string | undefined,
): string | undefined {
  if (!question.showCommentArea) {
    return undefined
  }

  if (question.showOtherItem && answerOrigin === 'other') {
    return undefined
  }
  return otherAnswer
}

/**
 * radiogroup の回答を文字列に parse
 */
export function parseRadiogroupAnswer(question: QuestionRadiogroupModelData, answerOrigin: string | undefined) {
  return parseSingleChoiceAnswer(question, answerOrigin)
}

/**
 * radiogroup の「その他」の回答を文字列に parse
 */
export function parseRadiogroupOtherAnswer(question: QuestionRadiogroupModelData, answerOrigin: string | undefined, otherAnswer: string | undefined) {
  return parseSingleChoiceOtherAnswer(question, answerOrigin, otherAnswer)
}

/**
 * radiogroup の「コメント」の回答を文字列に parse
 * NOTE:
 * - references - https://www.notion.so/asobica/surveyjs-comment-other-328c7e12081f42ea833fa0e59d004a52
 * - radiogroupと、checkboxはshowOtherItemがあり、これが、showCommentAreaも合わせて二つともtrueの場合にコメントとして入力した値の入り方がことなる
 */
export function parseRadiogroupCommentAnswer(question: QuestionRadiogroupModelData, answerOrigin: string | undefined, otherAnswer: string | undefined) {
  return parseSingleChoiceCommentAnswer(question, answerOrigin, otherAnswer)
}

/**
 * checkbox の回答を文字列に parse
 * NOTE:
 * - references - https://www.notion.so/asobica/surveyjs-comment-other-328c7e12081f42ea833fa0e59d004a52
 * - radiogroupと、checkboxはshowOtherItemがあり、これが、showCommentAreaも合わせて二つともtrueの場合にコメントとして入力した値の入り方がことなる
 */
export function parseCheckboxAnswer(
  question: QuestionCheckboxModelData,
  answerOrigin: string[] | undefined,
): string | undefined {
  if (answerOrigin === undefined) {
    return undefined
  }

  const answers = []
  for (const answer of answerOrigin) {
    if (answer === 'other') {
      answers.push('その他')
      continue
    }

    const choice = question.choices.find((choice) => {
      const choiceCast = choice as {
        text: string
        value: string
      }
      return choiceCast.value === answer
    })
    if (choice !== undefined) {
      const choiceCast = choice as {
        text: string
        value: string
      }
      answers.push(choiceCast.text)
    } else if (question.showOtherItem && question.showCommentArea) {
      answers.push('その他')
    }
  }

  if (answers.length > 0) {
    return answers.join(' ')
  }
  return undefined
}

/**
 * checkbox の「その他」の回答を文字列に parse
 */
export function parseCheckboxOtherAnswer(question: QuestionCheckboxModelData, answerOrigin: string[] | undefined, otherAnswer: string | undefined) {
  if (answerOrigin === undefined || !question.showOtherItem) {
    return undefined
  }

  if (!question.showCommentArea) {
    return otherAnswer
  }

  const questionIds = new Set(question.choices.map((choice) => {
    if (typeof choice === 'object') {
      return choice.value
    }
    return choice
  }))
  for (const answerItem of answerOrigin) {
    if (answerItem === 'other') {
      return otherAnswer
    }
    if (!questionIds.has(answerItem)) {
      return answerItem
    }
  }

  return undefined
}

/**
 * checkbox の「コメント」の回答を文字列に parse
 */
export function parseCheckboxCommentAnswer(question: QuestionCheckboxModelData, answerOrigin: string[] | undefined, otherAnswer: string | undefined) {
  if (!question.showCommentArea) {
    return undefined
  }

  if (question.showOtherItem && answerOrigin && answerOrigin.includes('other')) {
    return undefined
  }
  return otherAnswer
}

/**
 * 「コメント」の回答を文字列に parse
 * - radiogroup と checkbox の場合は、回答の取得方法が異なる @see parseRadiogroupCommentAnswer, parseCheckboxCommentAnswer
 */
export function parseCommonCommentAnswerExcludeRadiogroupAndCheckbox(question: SurveyElementJson, commentAnswer: string | undefined) {
  if (!hasShowCommentArea(question)) {
    return undefined
  }
  return commentAnswer
}

/**
 * boolean の回答を文字列に parse
 */
export function parseBooleanAnswer(answerOrigin: boolean | undefined) {
  if (answerOrigin === undefined) {
    return undefined
  }
  if (typeof answerOrigin === 'boolean') {
    return answerOrigin ? 'はい' : 'いいえ'
  }
  return undefined
}

/**
 * imagepicker の回答を parse
 */
export function parseImagepickerAnswer(
  question: QuestionImagepickerModelData,
  answerOrigin: string | string[] | undefined,
): QuestionImagepickerModelDataChoice | QuestionImagepickerModelDataChoice[] | undefined {
  if (answerOrigin === undefined) {
    return undefined
  }

  function getPickedChoices(choices: QuestionImagepickerModelDataChoice[], answer: string): QuestionImagepickerModelDataChoice | undefined {
    return choices.find((choice) => {
      return choice.value === answer
    })
  }

  if (isArray(answerOrigin)) {
    return answerOrigin.map((answer) => {
      return getPickedChoices(question.choices ?? [], answer)
    }).filter((choice): choice is QuestionImagepickerModelDataChoice => choice !== undefined)
  } else {
    return getPickedChoices(question.choices ?? [], answerOrigin)
  }
}

/**
 * file の回答を parse
 */
export function parseFileAnswer(answerOrigin: SurveyImageAnswerValue[] | undefined) {
  if (answerOrigin === undefined) {
    return undefined
  }

  if (answerOrigin.length > 0) {
    return answerOrigin[0]
  }
  return undefined
}

/**
 * 「コメント」の回答を文字列に parse
 * question.type が text, comment以外もコメント入力が可能になったので parseCommonXxxを作る
 *
 * TODO: 現在 radiogroup, checkbox, dropdown のみ対応だけど
 * 設問の追加時に漏れそうなので形で守りたい。
 */
export function parseCommentAnswer(question: SurveyElementJson, surveyAnswer: SurveyAnswerFlattenedRecord) {
  const questionId = question.name
  const answerValue = surveyAnswer[questionId]
  const answerCommentValue = surveyAnswer[`${questionId}-Comment`]
  const typedAnswerComment = getTypedAnswerCommentValue(answerCommentValue)

  switch (question.type) {
  case 'radiogroup': {
    const typedAnswer = getTypedAnswerValue(question, answerValue)
    const text = parseRadiogroupCommentAnswer(question, typedAnswer, typedAnswerComment)
    if (text !== undefined) {
      return ({
        text,
      })
    }
    return undefined
  }
  case 'checkbox': {
    const typedAnswer = getTypedAnswerValue(question, answerValue)
    const text = parseCheckboxCommentAnswer(question, typedAnswer, typedAnswerComment)
    if (text !== undefined) {
      return ({
        text,
      })
    }
    return undefined
  }
  case 'dropdown': {
    const typedAnswer = getTypedAnswerValue(question, answerValue)
    const text = parseDropdownCommentAnswer(question, typedAnswer, typedAnswerComment)
    if (text !== undefined) {
      return ({
        text,
      })
    }
    return undefined
  }
  default: {
    // question.type が text, comment以外もコメント入力が可能になったので parseCommonXxxを作る
    const text = parseCommonCommentAnswerExcludeRadiogroupAndCheckbox(question, typedAnswerComment)
    if (text !== undefined) {
      return ({
        text,
      })
    }
    return undefined
  }
  }
}

/**
 * 「その他」の回答を文字列に parse
 *
 * TODO: 現在 radiogroup, checkbox, dropdown のみ対応だけど
 * 設問の追加時に漏れそうなので形で守りたい。
 */
export function parseOtherAnswer(question: SurveyElementJson, surveyAnswer: SurveyAnswerFlattenedRecord) {
  const questionId = question.name
  const answerValue = surveyAnswer[questionId]
  const answerCommentValue = surveyAnswer[`${questionId}-Comment`]
  const typedAnswerComment = getTypedAnswerCommentValue(answerCommentValue)

  switch (question.type) {
  case 'radiogroup': {
    const typedAnswer = getTypedAnswerValue(question, answerValue)
    const text = parseRadiogroupOtherAnswer(question, typedAnswer, typedAnswerComment)
    if (text !== undefined) {
      return ({
        text,
      })
    }
    return undefined
  }
  case 'checkbox': {
    const typedAnswer = getTypedAnswerValue(question, answerValue)
    const text = parseCheckboxOtherAnswer(question, typedAnswer, typedAnswerComment)
    if (text !== undefined) {
      return ({
        text,
      })
    }
    return undefined
  }
  case 'dropdown': {
    const typedAnswer = getTypedAnswerValue(question, answerValue)
    const text = parseDropdownOtherAnswer(question, typedAnswer, typedAnswerComment)
    if (text !== undefined) {
      return ({
        text,
      })
    }
    return undefined
  }
  default: {
    return undefined
  }
  }
}

/**
 * ranking 回答を parse
 */
export function parseRankingAnswer(
  question: QuestionRankingModelData,
  value: string[] | undefined,
): string | undefined {
  if (!Array.isArray(value)) {
    return undefined
  }
  const choices = question.choices
  return value.map((choiceValue, index) => {
    const choice = choices.find((c: QuestionChoiceType) =>
      typeof c === 'string' ? c === choiceValue : c.value === choiceValue,
    )
    const displayText = typeof choice === 'string' ? choice : choice?.text || choiceValue
    return `${index + 1}:${displayText}`
  }).join(' ')
}

/**
 * dropdown の回答を文字列に parse
 */
export function parseDropdownAnswer(question: QuestionDropdownModelData, answerOrigin: string | undefined) {
  return parseSingleChoiceAnswer(question, answerOrigin)
}

/**
 * dropdown の「その他」の回答を文字列に parse
 */
export function parseDropdownOtherAnswer(question: QuestionDropdownModelData, answerOrigin: string | undefined, otherAnswer: string | undefined) {
  return parseSingleChoiceOtherAnswer(question, answerOrigin, otherAnswer)
}

/**
 * dropdown の「コメント」の回答を文字列に parse
 */
export function parseDropdownCommentAnswer(question: QuestionDropdownModelData, answerOrigin: string | undefined, otherAnswer: string | undefined) {
  return parseSingleChoiceCommentAnswer(question, answerOrigin, otherAnswer)
}

/**
 * 回答を文字列に parse
 * panelのネストした回答はサポートしないのでflattenedされてないデータは渡さないようにすること
 */
export function parseAnswer(question: SurveyElementJson, surveyAnswer: SurveyAnswerFlattenedRecord): ParsedSurveyCsvFlattenedAnswerValue | undefined {
  const questionId = question.name
  const answerValue = surveyAnswer[questionId]

  switch (question.type) {
  case 'radiogroup': {
    const typedAnswer = getTypedAnswerValue(question, answerValue)
    const text = parseRadiogroupAnswer(question, typedAnswer)
    if (text !== undefined) {
      return ({
        text,
      })
    }
    return undefined
  }
  case 'checkbox': {
    const typedAnswer = getTypedAnswerValue(question, answerValue)
    const text = parseCheckboxAnswer(question, typedAnswer)
    if (text !== undefined) {
      return ({
        text,
      })
    }
    return undefined
  }
  case 'boolean': {
    const typedAnswer = getTypedAnswerValue(question, answerValue)
    const text = parseBooleanAnswer(typedAnswer)
    if (text !== undefined) {
      return ({
        text,
      })
    }
    return undefined
  }
  case 'ranking': {
    const typedAnswer = getTypedAnswerValue(question, answerValue)
    const text = parseRankingAnswer(question, typedAnswer)
    if (text !== undefined) {
      return ({
        text,
      })
    }
    return undefined
  }
  case 'imagepicker': {
    const typedAnswer = getTypedAnswerValue(question, answerValue)
    const answer = parseImagepickerAnswer(question, typedAnswer)
    if (answer === undefined) {
      return undefined
    }

    if (isArray(answer)) {
      return answer.map((a) => {
        return {
          text: a.text ?? a.imageLink,
          link: a.imageLink,
        }
      })
    } else {
      return ({
        text: answer.text ?? answer.imageLink,
        link: answer.imageLink,
      })
    }
  }
  case 'file': {
    const typedAnswer = getTypedAnswerValue(question, answerValue)
    const answer = parseFileAnswer(typedAnswer)
    if (answer !== undefined) {
      return ({
        text: answer.name,
        link: answer.content,
      })
    }
    return undefined
  }
  case 'dropdown': {
    const typedAnswer = getTypedAnswerValue(question, answerValue)
    const text = parseDropdownAnswer(question, typedAnswer)
    if (text !== undefined) {
      return ({
        text,
      })
    }
    return undefined
  }
  default: {
    const text = answerValue ? String(answerValue) : undefined
    if (text !== undefined) {
      return ({
        text,
      })
    }
    return undefined
  }
  }
}

/**
 * 選択系の質問で選ばれた choiceId を得る（単体）
 */
export function findChoicedId(question: SurveyElementJson, answer: SurveyAnswerValue) {
  switch (question.type) {
  case 'radiogroup':
  case 'dropdown': {
    if (typeof answer === 'string') {
      if (checkULID(answer) || answer === 'other') {
        return answer
      }
    }
    return
  }
  case 'imagepicker': {
    if (typeof answer === 'string') {
      if (checkULID(answer)) {
        return answer
      }
    }
    return
  }
  case 'rating': {
    if (typeof answer === 'number') {
      return `${answer}`
    }
    return
  }
  case 'boolean': {
    if (typeof answer === 'boolean') {
      return `${answer}`
    }
    return
  }
  default:
    return
  }
}

/**
 * 選択系の質問で選ばれた choiceId を得る（複数）
 */
export function findChoicedIds(question: SurveyElementJson, answer: SurveyAnswerValue) {
  switch (question.type) {
  case 'checkbox': {
    if (Array.isArray(answer)) {
      const choiceIds = []
      for (const item of answer) {
        if (typeof item === 'string') {
          if (checkULID(item) || item === 'other') {
            choiceIds.push(item)
          }
        }
      }
      return choiceIds
    }
    return
  }
  default:
    return
  }
}
